import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
} from "@angular/core";

import { Subscription } from "rxjs";

import { ProductEnvironmentSimpleAttribute } from "@hermes/api-model-product-environment";

import {
    filterUnwantedValue,
    getAttributeDataFirstValue,
    transformValueWithDataIfValueGreaterThanOne,
} from "../../../helpers/agec-helper";
import { ReplacePercentPipe } from "../../../pipe/replace-percent.pipe";

@Component({
    selector: "h-composition-attribute",
    templateUrl: "./composition-attribute.component.html",
    styleUrls: ["./composition-attribute.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompositionAttributeComponent implements OnChanges, OnDestroy {
    @Input({ required: true })
    public compositionAttributes!: ProductEnvironmentSimpleAttribute[];

    public compositionTexts: string[] = [];

    public codesDangerousSubstance: string[] = [];

    public codesDangerousSubstancePackaging: string[] = [];

    public CONTAINS_DANGEROUS_SUBSTANCE = "CONTAINS_DANGEROUS_SUBSTANCE";

    public CONTAINS_DANGEROUS_SUBSTANCE_PACKAGING_PERFUME =
        "CONTAINS_DANGEROUS_SUBSTANCE_PACKAGING_PERFUME";

    public CONTAINS_DANGEROUS_SUBSTANCE_PACKAGING =
        "CONTAINS_DANGEROUS_SUBSTANCE_PACKAGING";

    private subscriptions: Subscription = new Subscription();

    private compositionAttributeTransformer: Record<
        string,
        (attribute: ProductEnvironmentSimpleAttribute) => string | undefined
    > = {
        RECYCLED_MATERIAL: (attribute) => this.getRecycledMaterial(attribute),
        RECYCLED_MATERIAL_PACKAGING_PERFUME: (attribute) =>
            this.getRecycledMaterial(attribute),
        RECYCLED_MATERIAL_PACKAGING: (attribute) =>
            this.getRecycledMaterial(attribute),
        SYNTHETIC_FIBERS: (attribute) => this.getSyntheticFibers(attribute),
        WEIGHT_PRECIOUS_METALS: (attribute) =>
            transformValueWithDataIfValueGreaterThanOne(attribute),
        WEIGHT_RARE_EARTH: (attribute) =>
            transformValueWithDataIfValueGreaterThanOne(attribute),
        CONTAINS_DANGEROUS_SUBSTANCE: (attribute) =>
            this.getCodeContainsDangerouSubstance(attribute),
        LIST_DANGEROUS_SUBSTANCE: (attribute) =>
            this.setListDangerousSubstances(attribute),
        CONTAINS_DANGEROUS_SUBSTANCE_PACKAGING_PERFUME: (attribute) =>
            this.getCodeContainsDangerouSubstance(attribute),
        LIST_DANGEROUS_SUBSTANCE_PACKAGING_PERFUME: (attribute) =>
            this.setListDangerousSubstancesPackaging(attribute),
        CONTAINS_DANGEROUS_SUBSTANCE_PACKAGING: (attribute) =>
            this.getCodeContainsDangerouSubstance(attribute),
        LIST_DANGEROUS_SUBSTANCE_PACKAGING: (attribute) =>
            this.setListDangerousSubstancesPackaging(attribute),
    };

    constructor(private replacePercentPipe: ReplacePercentPipe) {}

    public ngOnChanges(changes: SimpleChanges) {
        if (
            changes.compositionAttributes &&
            changes.compositionAttributes.currentValue
        ) {
            this.compositionTexts = this.compositionAttributes
                .map((compositionAttribute) => {
                    const transformFunction =
                        this.compositionAttributeTransformer[
                            compositionAttribute.code
                        ];
                    return transformFunction
                        ? transformFunction(compositionAttribute)
                        : undefined;
                })
                .filter(filterUnwantedValue);
        }
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    private getRecycledMaterial(
        recycledMaterialAttribute: ProductEnvironmentSimpleAttribute,
    ): string {
        const recycledMaterialPercentage = getAttributeDataFirstValue(
            recycledMaterialAttribute,
        );

        const returnTradForNoMaterialRecycled =
            recycledMaterialAttribute.code ===
                "RECYCLED_MATERIAL_PACKAGING_PERFUME" ||
            recycledMaterialAttribute.code === "RECYCLED_MATERIAL_PACKAGING"
                ? $localize`:@@hermes.material.packaging:L'emballage de ce produit ne contient pas de matière recyclée`
                : $localize`:@@hermes.environment.no-recycled-material:Ce produit ne contient pas de matière recyclée`;

        return !!recycledMaterialPercentage &&
            recycledMaterialPercentage !== "0"
            ? this.replacePercentPipe.transform(
                  recycledMaterialAttribute.value,
                  recycledMaterialPercentage,
              )
            : returnTradForNoMaterialRecycled;
    }

    private getSyntheticFibers(
        syntheticFibersAttribute: ProductEnvironmentSimpleAttribute,
    ): string | undefined {
        const recyclabilityDataValue = getAttributeDataFirstValue(
            syntheticFibersAttribute,
        );
        if (recyclabilityDataValue === "VAL_164") {
            return syntheticFibersAttribute.value;
        }
        return undefined;
    }

    private getCodeContainsDangerouSubstance(
        containsDangerouSubstanceAttribute: ProductEnvironmentSimpleAttribute,
    ): string | undefined {
        const containsDangerouSubstanceDataValue = getAttributeDataFirstValue(
            containsDangerouSubstanceAttribute,
        );
        if (containsDangerouSubstanceDataValue === "VAL_164") {
            return containsDangerouSubstanceAttribute.code;
        }

        return undefined;
    }
    /**
     * Set the list of dangerous substance code but return no value to be displayed
     *
     * @param listDangerousSubstancesAttribute
     * @returns undefined
     */
    private setListDangerousSubstances(
        listDangerousSubstancesAttribute: ProductEnvironmentSimpleAttribute,
    ): undefined {
        this.codesDangerousSubstance =
            listDangerousSubstancesAttribute.data ?? [];

        return undefined;
    }

    /**
     * Set the list of dangerous substance code for packaging but return no value to be displayed
     *
     * @param listDangerousSubstancesAttribute
     * @returns undefined
     */
    private setListDangerousSubstancesPackaging(
        listDangerousSubstancesAttribute: ProductEnvironmentSimpleAttribute,
    ): undefined {
        this.codesDangerousSubstancePackaging =
            listDangerousSubstancesAttribute.data ?? [];

        return undefined;
    }
}
